/**
 * UpsellModal Component
 * Post-add modal offering complementary products
 */

import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Bolt as BoltIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { SKUS, formatPrice, type Sku } from '../../lib/catalog';
import { useCartStore } from '../../store/cart';
import { trackUpsellShown, trackUpsellAccept, trackUpsellDismiss } from '../../lib/analytics';

interface UpsellModalProps {
  open: boolean;
  onClose: () => void;
  triggeredBy?: string; // SKU ID that triggered the upsell
}

// Define upsell recommendations based on what was added
const UPSELL_CONFIG: Record<string, string[]> = {
  core_build: ['deliverability', 'ops_enablement'],
  deliverability: ['ops_enablement', 'audit_vault'],
  ops_enablement: ['deliverability', 'maintenance_std'],
  default: ['deliverability', 'ops_enablement'],
};

const UpsellModal: React.FC<UpsellModalProps> = ({ open, onClose, triggeredBy }) => {
  const theme = useTheme();
  const { items, add } = useCartStore();

  // Get upsell recommendations
  const getUpsellSkus = (): Sku[] => {
    const recommendations = UPSELL_CONFIG[triggeredBy || 'default'] || UPSELL_CONFIG.default;
    const cartSkuIds = items.map(item => item.skuId);
    
    return recommendations
      .filter(skuId => !cartSkuIds.includes(skuId))
      .map(skuId => SKUS[skuId])
      .filter((sku): sku is Sku => sku !== undefined)
      .slice(0, 2); // Max 2 upsells
  };

  const upsellSkus = getUpsellSkus();

  // Track upsell modal shown
  useEffect(() => {
    if (open && upsellSkus.length > 0) {
      upsellSkus.forEach(sku => {
        trackUpsellShown(sku.id, sku.name);
      });
    }
  }, [open, upsellSkus]);

  const handleAccept = (sku: Sku) => {
    add(sku.id, 1);
    trackUpsellAccept(sku.id, sku.name, sku.unitAmount);
  };

  const handleDismiss = () => {
    upsellSkus.forEach(sku => {
      trackUpsellDismiss(sku.id, sku.name);
    });
    onClose();
  };

  // Don't show if no upsells available
  if (upsellSkus.length === 0) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <BoltIcon color="primary" />
        <Typography variant="h6" color="primary">
          Supercharge Your Setup
        </Typography>
      </Box>

      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="body1" color="text.secondary">
          Customers who purchased similar items also added these to maximize their results:
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {upsellSkus.map((sku) => {
            const isInCart = items.some(item => item.skuId === sku.id);
            
            return (
              <Card
                key={sku.id}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {sku.name}
                        </Typography>
                        {sku.popular && (
                          <Chip
                            label="Popular"
                            size="small"
                            color="warning"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                      
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1.5 }}
                      >
                        {sku.description}
                      </Typography>

                      {sku.features && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
                          {sku.features.slice(0, 3).map((feature, idx) => (
                            <Chip
                              key={idx}
                              icon={<CheckIcon sx={{ fontSize: 14 }} />}
                              label={feature}
                              size="small"
                              variant="outlined"
                              sx={{ height: 24, fontSize: '0.7rem' }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>

                    <Box sx={{ textAlign: 'right', ml: 2 }}>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        {formatPrice(sku.unitAmount)}
                      </Typography>
                      <Button
                        variant={isInCart ? 'outlined' : 'contained'}
                        size="small"
                        onClick={() => !isInCart && handleAccept(sku)}
                        disabled={isInCart}
                        startIcon={isInCart ? <CheckIcon /> : null}
                        sx={{ mt: 1 }}
                      >
                        {isInCart ? 'Added' : 'Add'}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={handleDismiss} color="inherit">
          No thanks, continue to cart
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpsellModal;
